import { Message, Divider, Radio, Text, useToast, useDesignLanguagePerClass, DividerMargin, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { ModuleContainer } from '../ModuleContainer'
import { app } from '../../env'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useFlag } from '@afterpaytouch/feature'
import { Features, InitiateThreeDSErrorCode, NewCard, NotificationError, ThreeDSChallengeStatus, TopazError, TopazStatus } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import { Cohort } from '@afterpaytouch/portal-api'
import {
  finaliseThreeDSVerification,
  initiateThreeDSVerification,
  resetThreeDSState,
  selectRecoverableDeclinesPaymentMethod,
  selectPurchaseMethod,
  setLastSuccessfulSetup,
  useAppDispatch,
  useApplePayForCashCohort,
  useApplePayPayment,
  useCheckoutState,
  useCohort,
  useConsumerSelector,
  useHasSavedPaymentMethods,
  useIsApplePay,
  useIsOverduePaymentsFlow,
  useIsRecoverableFlow,
  useLastSuccessfulSetup,
  useRecoverableFlowSelectedOrder,
  useRecoverableTerminalErrorsState,
  useSelectedPaymentMethod,
  useThreeDsState,
  useTopazState,
  useTraceId,
  retrieveCards,
  useIsAfterpayFinanceFeeEligible,
  useIsApplePaylater,
  useSelectedFeature,
  useIsApplePaylaterWithApplePayRepayment,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { getNewCardDetails } from './utils'
import { NewPaymentMethodFormSchema, TopazErrorContext } from './types'
import { useNonInitialEffect } from '../../utils/hooks'
import { Feature, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { valueIsNotNullOrUndefined } from '../../utils/validation'
import { parseTopazLogEventProperties } from '../../utils/topaz'
import { useApplePayTooltipClassic } from '../PaymentMethodApplePay/useApplePayTooltip'
import { useModuleContainerProps, useBuildCardForm, useHandleCardPayment, useHandleApplePay } from './hooks'
import { CardPaymentOption, CardPaymentOptionLabelRedesigned } from './NewCardPayment'
import { ApplePayPaymentOption } from './NewApplePayPayment'
import { AddCardForm } from './AddCardForm'
import { ApplePayButtonHeading } from '../../flow/apple-paylater/components/ApplePayButtonHeading'
import { selectRecoverableCardServiceErrorMessage, selectThreeDSErrorMessage, selectTopazErrorMessage } from './errorMessageSelectors'

export const useDefaultNewPaymentMethodSelection = (): 'card' | 'applePay' => {
  const isApplePay = useIsApplePay()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isApplePaylater = useIsApplePaylater()
  return isApplePay && !isAFF && !isApplePaylater ? 'applePay' : 'card'
}

interface Props {
  onSuccess: () => void
  onTerminalError: (reason: TopazError) => void
  onSubmit: (onSubmitted: boolean) => void
  formSubmitted: boolean
  displayCreditCardMessage: boolean
  onPaymentMethodChange?: (method: 'card' | 'applePay') => void
}
interface ApiErrors {
  topazErrors?: string
  threeDSErrors?: string
  creditCardServiceErrors?: string
}

export const NewPaymentMethod: FunctionComponent<Props> = ({
  onSuccess,
  onTerminalError,
  onSubmit,
  formSubmitted,
  displayCreditCardMessage,
  onPaymentMethodChange,
}) => {
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const feature = useSelectedFeature()
  const isApplePaylater = useIsApplePaylater()
  const defaultChecked = useDefaultNewPaymentMethodSelection()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const applePayEnabled = useApplePayPayment()
  const isApplePayEnabledForCashCohort = useApplePayForCashCohort()
  const isCashOnlyCohort = useCohort() === Cohort.CashOnly
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const showApplePay = (applePayEnabled || (isCashOnlyCohort && isApplePayEnabledForCashCohort && !hasSavedPaymentMethods)) && !isAFF && !isApplePaylater
  const { t } = useTranslation()
  const { profile } = useConsumerSelector()
  const { started } = useCheckoutState()
  const isOverduePaymentsFlow = useIsOverduePaymentsFlow()
  const { error: recoverableTerminalCurrentError, status: recoverableFlowStatus } = useRecoverableTerminalErrorsState()
  const { purchase } = useTopazState()
  const dispatch = useAppDispatch()
  const lastSuccessfulSetup = useLastSuccessfulSetup()
  const { textSize } = useCheckoutSizes()
  const recoverableFlowSelectedOrder = useRecoverableFlowSelectedOrder()
  const { toggleTooltipClassic, applePayTooltipRendererClassic, closeTooltip } = useApplePayTooltipClassic()
  const moduleContainerProps = useModuleContainerProps()
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const enabledCardTypes = started?.enabledCardTypes ?? recoverableFlowSelectedOrder?.enabledCardTypes
  const isThreeDSEnabled = useFlag(Features.CHECKOUT_THREE_DS_ENABLED)
  const [showThreeDSIframe, setShowThreeDSIframe] = useState(false)
  const [threeDSIframeURL, setThreeDSIframeURL] = useState('')
  const threeDsState = useThreeDsState()
  const isRecoverableFlow = useIsRecoverableFlow()
  const standardCheckoutTraceId = useTraceId()
  const setToast = useToast()

  const traceId = isRecoverableFlow ? lastSuccessfulSetup?.traceId : standardCheckoutTraceId
  const [errors, setErrors] = useState<ApiErrors>({
    topazErrors: undefined,
    threeDSErrors: undefined,
    creditCardServiceErrors: undefined,
  })
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [paymentMethod, setPaymentMethod] = useState(showApplePay ? defaultChecked : 'card')

  useEffect(() => {
    onPaymentMethodChange?.(paymentMethod)
  }, [paymentMethod, onPaymentMethodChange])

  useEffect(() => {
    if (!showApplePay && paymentMethod === 'applePay') {
      setPaymentMethod('card')
    }
  }, [showApplePay, paymentMethod])
  const [showModalCloseButton, setShowModalCloseButton] = useState(true)
  const topazSelectedPaymentMethod = useSelectedPaymentMethod()
  const { submitted, schema, formData, formRef, handleOnChange, setIsTopazError, setDisplayErrors, setSubmitted } = useBuildCardForm({
    showApplePay,
    displayCreditCardMessage: displayCreditCardMessage || isAFF,
    feature,
    enabledCardTypes,
    errors,
  })

  const isApplePaylaterApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()

  const [notification, setNotification] = useState<NotificationError | null>(null)
  const initialPaymentMethod = topazSelectedPaymentMethod
  const revertToOriginalPaymentMethod = async (): Promise<void> => {
    dispatch(setLastSuccessfulSetup(undefined))
    if (initialPaymentMethod === undefined) {
      return
    }
    if (isRecoverableFlow) {
      await dispatch(selectRecoverableDeclinesPaymentMethod(initialPaymentMethod, false))
    } else {
      await dispatch(selectPurchaseMethod(initialPaymentMethod, false))
    }
  }
  const checkThreeDS = async (): Promise<void> => {
    if (isThreeDSEnabled) {
      const response = await dispatch(initiateThreeDSVerification(traceId))
      if ('errorCode' in response) {
        const { httpStatusCode, errorCode } = response
        logEvent(TrackingEvent.VIEWED_3DS_VERIFICATION_INITIATE_FAILED, { error: errorCode })
        handleThreeDSErrors(httpStatusCode)
        revertToOriginalPaymentMethod()
      }
    } else {
      onSuccess()
    }
  }

  const receiveMessage = (event): void => {
    let messageData
    try {
      messageData = JSON.parse(event.data)
    } catch {
      messageData = event?.data
    }
    if (messageData?.paymentsThreeDs?.userComplete === 'true' && event.origin === app.THREE_DS_REDIRECT_URL) {
      window.removeEventListener('message', receiveMessage, false)
      setShowModalCloseButton(false)
      dispatch(finaliseThreeDSVerification(threeDsState?.threeDSId, traceId)).then((response) => {
        if (response !== undefined && typeof response === 'object' && response !== null && 'errorCode' in response) {
          handleThreeDSErrors(InitiateThreeDSErrorCode.PAYMENT_REQUIRED)
          setShowThreeDSIframe(false)
          logEvent(TrackingEvent.NEW_CARD_ADDED_3DS_FAILED, { error: response?.errorCode })
          revertToOriginalPaymentMethod()
        } else {
          logEvent(TrackingEvent.NEW_CARD_ADDED_3DS_PASSED, { traceId })
          setShowThreeDSIframe(false)
          dispatch(resetThreeDSState())
          onSuccess()
        }
      })
    }
  }

  useEffect(() => {
    switch (threeDsState?.challenge?.status) {
      case ThreeDSChallengeStatus.ChallengeRequired:
        setThreeDSIframeURL(threeDsState.challenge?.challengeUri ?? '')
        setShowThreeDSIframe(true)
        logEvent(TrackingEvent.VIEWED_3DS_VERIFICATION_INITIATE, { challengeRequired: true })
        window.addEventListener('message', receiveMessage, false)
        break
      case ThreeDSChallengeStatus.ChallengeNotRequired:
        logEvent(TrackingEvent.VIEWED_3DS_VERIFICATION_INITIATE, { challengeRequired: false })
        dispatch(resetThreeDSState())
        onSuccess()
        break
    }
    return () => {
      window.removeEventListener('message', receiveMessage, false)
    }
  }, [threeDsState])

  const failedToRetrieveCard = (): JSX.Element => (
    <>
      <Text size={textSize} bold>
        {t('paymentMethod:errors.failedToRetrieveCard.heading')}
      </Text>
      <Text size={textSize}>{t('paymentMethod:errors.failedToRetrieveCard.message')}</Text>
    </>
  )

  const handleThreeDSErrors = (statusCode): void => {
    const threeDsError = selectThreeDSErrorMessage(t, statusCode)

    setErrors({
      ...errors,
      threeDSErrors: threeDsError,
    })
    setDisplayErrors(true)
  }

  const handleTopazError = (purchase, displayErrors = true, triggerError = true): void => {
    setSubmitted(false)
    if (displayErrors) {
      if (triggerError) {
        setErrors({ ...errors, topazErrors: selectTopazErrorMessage(t, purchase.status) })
      }
      setIsTopazError(true)
      setDisplayErrors(true)
    }
  }

  useNonInitialEffect(() => {
    const handleTopazStateChanges = async (): Promise<void> => {
      if (purchase.status === TopazStatus.Saved) {
        await checkThreeDS()
        setSubmitted(false)
      } else if (purchase.status === TopazStatus.FailedToRetrieveNewCard) {
        logEvent(
          TrackingEvent.CHECKOUT_TOPAZ_ERROR,
          parseTopazLogEventProperties({
            topazStatus: TopazStatus[TopazStatus.FailedToRetrieveNewCard],
            errorMessage: purchase.portalApiError?.message,
            errorCode: purchase.portalApiError?.errorCode,
            context: TopazErrorContext.retrieveNewCard,
          })
        )
        if (valueIsNotNullOrUndefined(purchase?.selected)) {
          revertToOriginalPaymentMethod()
          await checkThreeDS()
          handleTopazError(purchase, false)
        } else {
          handleTopazError(purchase, true, false)
        }
        setToast({
          message: failedToRetrieveCard,
          kind: 'Failure',
        })
      } else if (purchase.status === TopazStatus.ExceededMaxAttemptsEnteringDetails) {
        onTerminalError(TopazError.ExceededMaxAttemptsEnteringDetails)
      } else if (purchase.status > TopazStatus.Saved) {
        if (purchase.status === TopazStatus.DuplicateCard) {
          await dispatch(retrieveCards())
          logEvent(TrackingEvent.NEW_PAYMENT_METHOD_UPDATE_TRACE_ID, {
            traceId,
          })
        }

        handleTopazError(purchase)
        if (purchase.portalApiError !== undefined) {
          const errorMessage = purchase.portalApiError.message
          const context = TopazErrorContext.saveNewCardPaymentMethod
          logEvent(
            TrackingEvent.CHECKOUT_TOPAZ_ERROR,
            parseTopazLogEventProperties({
              topazStatus: TopazStatus[purchase.status],
              errorCode: purchase.portalApiError.errorCode,
              context,
              errorMessage,
            })
          )
        }
      }
    }

    handleTopazStateChanges()
  }, [purchase.status])

  useNonInitialEffect(() => {
    const handleCreditCardStateChanges = (): void => {
      if (recoverableTerminalCurrentError !== undefined) {
        setSubmitted(false)
        setDisplayErrors(true)
        setErrors((currErrs) => ({
          ...currErrs,
          creditCardServiceErrors: selectRecoverableCardServiceErrorMessage(t, recoverableTerminalCurrentError.error),
        }))
      }
    }

    handleCreditCardStateChanges()
  }, [isRecoverableFlow, recoverableTerminalCurrentError])

  useEffect(() => {
    if (isRecoverableFlow && recoverableFlowStatus === RecoverableTerminalErrorStatus.LackOfEligiblePaymentMethod) {
      setNotification({
        heading: t('paymentMethod:errors.lackOfEligiblePaymentMethod.heading'),
        message: t('paymentMethod:errors.lackOfEligiblePaymentMethod.message'),
        severe: false,
      })
    } else {
      setNotification(null)
    }
  }, [isRecoverableFlow, recoverableFlowStatus])

  useNonInitialEffect(() => {
    logEvent(TrackingEvent.TOGGLE_ADD_NEW_PAYMENT_METHOD_CARD_ADDRESS)
  }, [formData?.billingAndProfileAddressEqual])

  useEffect(() => {
    onSubmit(submitted)
  }, [submitted])

  const threeDSOnClose = (): void => {
    setShowThreeDSIframe(false)
    setSubmitted(false)
    setErrors({
      ...errors,
      threeDSErrors: t('paymentMethod:threeDS.errors.userCancelled'),
    })
    setDisplayErrors(true)
    window.removeEventListener('message', receiveMessage, false)
  }

  const handleApplePayPaymentMethod = useHandleApplePay({
    setSubmitted,
    onSuccess,
  })

  const handleCardPaymentMethod = useHandleCardPayment({
    setSubmitted,
    formRef,
    onSuccess,
  })

  const submit = async (payload: NewCard): Promise<void> => {
    if (paymentMethod === 'card') {
      await handleCardPaymentMethod(payload)
    } else {
      await handleApplePayPaymentMethod()
    }
  }

  const handleOnSubmit = (data: NewPaymentMethodFormSchema): void => {
    setIsTopazError(false)
    setDisplayErrors(false)
    setErrors({
      topazErrors: undefined,
      creditCardServiceErrors: undefined,
      threeDSErrors: undefined,
    })
    setShowModalCloseButton(true)
    const payload = getNewCardDetails(data, profile?.contactAddress)
    submit(payload)
  }

  const handleFormSubmit = async (data: NewPaymentMethodFormSchema): Promise<void> => {
    if (paymentMethod !== 'card') {
      return
    }

    if (submitted || showThreeDSIframe) {
      return
    }

    handleOnSubmit(data)
  }

  useNonInitialEffect(() => {
    handleOnSubmit(formData)
  }, [formSubmitted])

  const dividerMargin: DividerMargin = isArcadeDesignLanguage ? 'Regular' : 'No'
  return (
    <>
      {notification !== null && (
        <div className={designLanguageStyles.notificationWrapper}>
          <ModuleContainer {...moduleContainerProps}>
            <Message
              iconName='Card'
              kind='Basic'
              heading={notification?.heading}
              description={notification?.message}
              testNameSpace={'new-payment-method-message'}
            />
            {isOverduePaymentsFlow && (
              <TrackOnMount
                eventName={TrackingEvent.VIEWED_OVERDUE_LACK_OF_ELIGIBLE_PAYMENT_METHOD_ERROR}
                eventProperties={{ errorCode: recoverableTerminalCurrentError }}
              />
            )}
          </ModuleContainer>
        </div>
      )}
      {isArcadeDesignLanguage && paymentMethod === 'card' && !showApplePay && (
        <ModuleContainer {...moduleContainerProps} extraBottomMargin={true}>
          <CardPaymentOptionLabelRedesigned selectedPaymentMethod='card' feature={feature} enabledCardTypes={enabledCardTypes} />
        </ModuleContainer>
      )}
      {isApplePaylaterApplePayRepaymentEnabled && feature === Feature.PBI && <ApplePayButtonHeading />}
      <Radio.Group id='payment-method' name='payment-method' defaultValue={defaultChecked}>
        {showApplePay && (
          <CardPaymentOption
            selectedPaymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            closeApplepayTooltip={closeTooltip}
            enabledCardTypes={enabledCardTypes}
            feature={feature}
            formData={formData}
          />
        )}
        {paymentMethod === 'card' && (
          <AddCardForm
            showApplePay={showApplePay}
            formRef={formRef}
            schema={schema}
            handleOnChange={handleOnChange}
            onSubmit={handleFormSubmit}
            submitted={submitted}
            showThreeDSIframe={showThreeDSIframe}
            threeDSIframeURL={threeDSIframeURL}
            threeDSOnClose={threeDSOnClose}
            showModalCloseButton={showModalCloseButton}
          />
        )}
        {showApplePay && <Divider margin={dividerMargin} kind={isArcadeDesignLanguage ? 'Space' : 'Hairline'} />}
        {showApplePay && (
          <ApplePayPaymentOption
            selectedPaymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            closeTooltipClassic={closeTooltip}
            toggleTooltipClassic={toggleTooltipClassic}
          />
        )}
        {!isArcadeDesignLanguage && applePayTooltipRendererClassic()}
      </Radio.Group>
    </>
  )
}
